// React
import React, { useState, useEffect, useMemo, memo } from 'react';

// utils
import { CleverTapNavigation } from 'aurora-utils/cleverTap.eventhelper';
import PropTypes from 'prop-types';
import { NavLink } from 'react-router-dom';

// components

import Button from '@material-ui/core/Button';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';

// Redux
import { useDispatch, useSelector } from 'react-redux';
import { fetchProfile } from 'aurora-composition/modules/user/actions';

// Styles
import { makeStyles } from '@material-ui/core/styles';
import { keyEmptyCheck } from 'aurora-utils/utils';
import './navigation.css';
import ADGMImage from '../ADGMImage';

const useStyles = makeStyles(theme => ({
  root: {
    margin: '0 2px',
  },

  navButton: {
    fontWeight: '400',
    textTransform: 'capitalize',
    borderBottom: '2px solid transparent',
    borderTop: '2px solid transparent',
    // padding: '8px 12px',
    borderRadius: '10px',
    position: 'relative',
    transition: 'none',
    '&:hover': {
      background: theme.palette.primary.grey,
    },

    '&::after': {
      display: 'none',
    },
  },
  navButtonActive: {
    borderBottom: `5px solid ${theme.palette.primary.main}`,
    borderRadius: '0',
  },
  navMenuDropdown: {
    minWidth: '180px',
    // padding: '8px',
    borderRadius: '0px !important',
    border: '1px solid #EBEEF3',
    boxShadow: 'none',
  },
  menuList: {
    padding: '0px',
    '& .MuiListItem-root': {
      borderTop: '1px solid rgba(235, 238, 243, 0.5)',
      padding: '16px',
      fontWeight: '500',
      fontSize: '14px',
      lineHeight: '21px',
      '&:nth-child(1)': {
        border: 'none',
      },
      '&:hover': {
        color: theme.palette.primary.main,
      },
    },
  },
  headingText: {
    textTransform: 'none',
    fontWeight: 400,
    fontSize: '14px',
    color: theme.palette.primary.black80,
  },
  headingTextLogin: {
    textTransform: 'none',
    fontWeight: 400,
    fontSize: '14px',
    color: theme.palette.primary.foreground,
  },
  activeSelected: {
    fontWeight: 700,
    '&::after': {
      content: `''`,
      position: 'absolute',
      bottom: '2px',
      background: '#0088FF',
      left: '8px',
      right: '8px',
      height: '2px',
    },
  },
  badge: {
    height: '5px',
    width: '5px',
    borderRadius: '2px',
    backgroundColor: '#0088FF',
    alignSelf: 'start',
  },
  myProject: {
    display: 'flex',
    flexDirection: 'row',
    padding: 0,
    margin: 0,
  },
  iconsown: {
    width: '16px',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: '3px',
  },
}));

const LinkBtn = React.forwardRef((props, ref) => <NavLink to={props.to} {...props} innerRef={ref} />);

const PATH_NAMES = {
  home: '/app/home',
  applications: '/app/applications',
  services: '/app/services',
  properties: '/app/properties',
  directory: '/app/directory',
  marketData: '/app/market-data-detail',
  contracts: '/app/contracts',
  // project: '/app/my-projects',
  project: '/app/projects-list',
};

// eslint-disable-next-line react/prop-types
const Navigation = ({ isGuest }) => {
  // const { location: { pathname = '' } = {} } = history;
  const classes = useStyles(isGuest);
  const [anchorEl, setAnchorEl] = useState(null);
  const [btnClickedName, setBtnClickedName] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [isVisible, setIsVisible] = useState(true);

  const open = Boolean(anchorEl);
  const dispatch = useDispatch();
  const user = useSelector(state => state.userReducer.user?.user);

  // eslint-disable-next-line no-unused-vars
  let loggedInUser = null;
  if (localStorage.getItem('selectedAccount')) {
    const adddata = localStorage?.selectedAccount;
    loggedInUser = JSON.parse(adddata);
  }

  useEffect(() => {
    dispatch(fetchProfile());
  }, []);
  // const locale = localStorage.getItem('lang');

  const isCompany = useMemo(
    () => (user?.companyUserSubTypes || []).some(company => company?.selected && company?.isBank),
    [user],
  );

  const MenuItemsMap = useMemo(
    () => ({
      properties: [
        {
          translationKey: 'Lease',
          href: `${PATH_NAMES?.properties}?type=lease`,
        },
        {
          translationKey: 'Ownership',
          href: `${PATH_NAMES?.properties}?type=ownership`,
        },
        ...(isCompany
          ? [
              {
                translationKey: 'mortgage',
                href: `${PATH_NAMES?.properties}?type=mortgage`,
              },
            ]
          : []),
      ],
      directory: [
        {
          translationKey: 'Projects',
          href: `${PATH_NAMES?.directory}?category=projects`,
        },
        // { translationKey: 'professions', href: `${PATH_NAMES?.directory}?category=professions` },
        // {
        //   translationKey: 'law_directory',
        //   href:
        //     locale === 'en' ?'' :''
        // },
      ],
      contracts: [
        {
          translationKey: 'Lease',
          href: `${PATH_NAMES?.contracts}?type=Lease`,
        },
        { translationKey: 'pma', href: `${PATH_NAMES?.contracts}?type=Pma` },
        {
          translationKey: 'off_plan',
          href: `${PATH_NAMES?.contracts}?type=Offplan`,
        },
        // { translationKey: 'musataha', href: `${PATH_NAMES?.contracts}?type=Musataha` },
        { translationKey: 'mortgage', href: `${PATH_NAMES?.contracts}?type=Mortgage` },
        { translationKey: 'POA', href: `${PATH_NAMES?.contracts}?type=Poa` },
      ],
      applications: [
        {
          translationKey: 'Applications',
          href: `${PATH_NAMES?.applications}?type=applications`,
        },
        {
          translationKey: 'tasks',
          href: `${PATH_NAMES?.applications}?type=tasks&taskStatus=PENDING`,
        },
      ],
    }),
    [isCompany],
  );

  const handleClick = (event, btnName) => {
    CleverTapNavigation(btnName);
    setAnchorEl(event.currentTarget);
    setBtnClickedName(btnName);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <div className={classes.root}>
      <Button
        className={isGuest ? classes.headingText : classes.headingTextLogin}
        onClick={() => CleverTapNavigation('home')}
        component={LinkBtn}
        to={PATH_NAMES?.home}
      >
        {keyEmptyCheck('home')}
      </Button>
      <Button
        className={[
          isGuest ? classes.headingText : classes.headingTextLogin,
          window.location.pathname === `/${localStorage.getItem('lang')}/app/applications` && classes.activeSelected,
        ]}
        id='basic-button'
        aria-controls={open ? 'basic-menu' : undefined}
        aria-haspopup='true'
        aria-expanded={open ? 'true' : undefined}
        onClick={event => handleClick(event, 'applications')}
      >
        {keyEmptyCheck('Applications')}
        <ADGMImage name='angle-small-down-bold-black.svg' alt='arrowdown' className={classes.iconsown} />
      </Button>
      <Button
        className={isGuest ? classes.headingText : classes.headingTextLogin}
        onClick={() => CleverTapNavigation('services')}
        component={LinkBtn}
        to={PATH_NAMES?.services}
      >
        {keyEmptyCheck('Services')}
      </Button>
      <Button
        className={[
          isGuest ? classes.headingText : classes.headingTextLogin,
          window.location.pathname === `/${localStorage.getItem('lang')}/app/properties` && classes.activeSelected,
        ]}
        id='basic-button'
        aria-controls={open ? 'basic-menu' : undefined}
        aria-haspopup='true'
        aria-expanded={open ? 'true' : undefined}
        onClick={event => handleClick(event, 'properties')}
      >
        {keyEmptyCheck('leasable_properties')}
        <ADGMImage name='angle-small-down-bold-black.svg' alt='arrowdown' className={classes.iconsown} />
      </Button>
      <Button
        className={[
          isGuest ? classes.headingText : classes.headingTextLogin,
          window.location.pathname === `/${localStorage.getItem('lang')}/app/directory` && classes.activeSelected,
        ]}
        id='basic-button'
        aria-controls={open ? 'basic-menu' : undefined}
        aria-haspopup='true'
        aria-expanded={open ? 'true' : undefined}
        onClick={event => handleClick(event, 'directory')}
      >
        {keyEmptyCheck('Directory')}
        <ADGMImage name='angle-small-down-bold-black.svg' alt='arrowdown' className={classes.iconsown} />
      </Button>
      {/* <Button
        className={isGuest ? classes.headingText : classes.headingTextLogin}
        onClick={() => CleverTapNavigation('market-data-detail')}
        component={LinkBtn}
        to='/app/market-data-detail'
      >
        {keyEmptyCheck('market_data')}
      </Button> */}
      {loggedInUser?.isDeveloper && (
        <Button
          className={isGuest ? classes.headingText : classes.headingTextLogin}
          onClick={() => {
            CleverTapNavigation('my_projects');
            if (isVisible) setIsVisible(false);
          }}
          component={LinkBtn}
          to={PATH_NAMES?.project}
        >
          {keyEmptyCheck('my_projects')}
          {isVisible && <div className={classes.badge} />}
        </Button>
      )}
      <Button
        className={[
          isGuest ? classes.headingText : classes.headingTextLogin,
          window.location.pathname === `/${localStorage.getItem('lang')}/app/contracts` && classes.activeSelected,
        ]}
        id='basic-button'
        aria-controls={open ? 'basic-menu' : undefined}
        aria-haspopup='true'
        aria-expanded={open ? 'true' : undefined}
        onClick={event => handleClick(event, 'contracts')}
      >
        {keyEmptyCheck('contracts')}
        <ADGMImage name='angle-small-down-bold-black.svg' alt='arrowdown' className={classes.iconsown} />
      </Button>
      <Menu
        id='simple-menu'
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        classes={{ paper: classes.navMenuDropdown, list: classes.menuList }}
      >
        {MenuItemsMap[btnClickedName]?.map(menuItem => (
          <>
            <MenuItem
              key={menuItem.translationKey}
              onClick={() => {
                CleverTapNavigation(btnClickedName);
                handleClose();
              }}
              {...(menuItem.translationKey === 'law_directory'
                ? { href: menuItem.href, component: 'a' }
                : { to: `${menuItem.href}`, component: LinkBtn })}
            >
              {keyEmptyCheck(menuItem.translationKey)}
            </MenuItem>
          </>
        ))}
      </Menu>
    </div>
  );
};

LinkBtn.propTypes = {
  to: PropTypes.string.isRequired,
};

export default memo(Navigation);
